import { useCallback, useEffect, useState } from "react";
import { Fingerprint, Copy, RefreshCw, Radio, Loader2, CheckCircle2, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FingerspotEnrollment from "@/components/FingerspotEnrollment";
import FingerspotAutoSync from "@/components/FingerspotAutoSync";
import { DeviceHealthDetailed } from "@/components/DeviceHealth";

const VERIFY_STYLE = {
  fingerprint: "bg-[#E7F0FF] text-[#0058C7]",
  password: "bg-[#F1ECFF] text-[#6D3BEB]",
  card: "bg-[#FFF1E6] text-[#B5590F]",
  face: "bg-[#E9F9EF] text-[#0E8A46]",
  vein: "bg-[#FDE9F1] text-[#B32D6B]",
};
const STATUS_STYLE = {
  scan_in: "bg-[#E9F9EF] text-[#0E8A46]",
  scan_out: "bg-[#FFF1E6] text-[#B5590F]",
  break_in: "bg-[#E7F0FF] text-[#0058C7]",
  break_out: "bg-[#F1F1F4] text-[#6E6E73]",
};
const INTERP_STYLE = {
  "Clock In": "bg-[#E9F9EF] text-[#0E8A46]",
  "Clock Out": "bg-[#EAF0FF] text-[#0058C7]",
  "Repeated Arrival — Ignored": "bg-[#F1F1F4] text-[#6E6E73]",
  "Duplicate — Ignored": "bg-[#F1F1F4] text-[#6E6E73]",
  "Extra Scan — Ignored": "bg-[#F1F1F4] text-[#6E6E73]",
  "System/Admin — Ignored": "bg-[#F1F1F4] text-[#6E6E73]",
  "Before Go-Live": "bg-[#F1F1F4] text-[#6E6E73]",
  Unmapped: "bg-[#FFF1E6] text-[#B5590F]",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function FingerspotIntegration() {
  const [status, setStatus] = useState(null);
  const [scans, setScans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapFor, setMapFor] = useState(null); // scan being mapped
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/fingerspot/webhook`;

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/integrations/fingerspot/status"),
        api.get("/integrations/fingerspot/scans?limit=50"),
      ]);
      setStatus(s.data);
      setScans(r.data);
    } catch {
      /* silent — auto-retries */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const openMap = (scan) => {
    setMapFor(scan);
    setPick("");
  };

  const doMap = async () => {
    if (!pick || !mapFor) return;
    setSaving(true);
    try {
      await api.put(`/employees/${pick}/fingerspot-pin`, { pin: mapFor.employee_pin });
      const emp = employees.find((e) => e.id === pick);
      toast.success(`PIN ${mapFor.employee_pin} → ${emp?.nama || "employee"}`);
      setMapFor(null);
      await load();
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const connected = (status?.total_events || 0) > 0;

  return (
    <div data-testid="fingerspot-page" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight sm:text-[28px]">
          <Fingerprint className="h-6 w-6 text-[#0071E3]" /> Fingerspot
        </h1>
        <p className="mt-0.5 text-[13px] text-[#8E8E93]">
          Manage devices, employee mapping, and Fingerspot integration.
        </p>
      </div>

      <DeviceHealthDetailed />

      <FingerspotEnrollment onChanged={() => api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {})} />

      <FingerspotAutoSync />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="liquid-glass-card space-y-4 p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-[15px] font-semibold tracking-tight">Device & Webhook</h2>
            <span
              data-testid="fingerspot-conn-status"
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium ${
                connected ? "bg-[#E9F9EF] text-[#0E8A46]" : "bg-[#F1F1F4] text-[#8E8E93]"
              }`}
            >
              {connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Radio className="h-3.5 w-3.5" />}
              {connected ? "Receiving events" : "Waiting for first scan"}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Device Name" value={status?.device_name || "—"} testid="fingerspot-device" />
            <Field label="Cloud ID" value={status?.cloud_id || "—"} mono testid="fingerspot-cloud-id" />
          </div>

          <div>
            <label className="mb-1 block text-[11.5px] font-semibold uppercase tracking-wide text-[#8E8E93]">
              Webhook Endpoint (HTTPS)
            </label>
            <div className="flex items-stretch gap-2">
              <input
                data-testid="fingerspot-webhook-url"
                readOnly
                value={webhookUrl}
                onFocus={(e) => e.target.select()}
                className="min-w-0 flex-1 rounded-xl border border-[#E1E1E6] bg-white/70 px-3 py-2 font-mono text-[12.5px] text-[#1D1D1F] outline-none focus:border-[#0071E3]"
              />
              <button
                data-testid="fingerspot-copy-btn"
                onClick={copy}
                className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#0071E3] px-3.5 text-[13px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98]"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
            </div>
            <p className="mt-1.5 text-[12px] text-[#8E8E93]">
              Paste this URL into the <span className="font-medium text-[#6E6E73]">“End Point Webhook”</span> field on Developer.Fingerspot.io.
            </p>
          </div>
        </div>

        <div className="liquid-glass-card space-y-4 p-5">
          <h2 className="text-[15px] font-semibold tracking-tight">Status</h2>
          <div>
            <p className="text-[11.5px] font-semibold uppercase tracking-wide text-[#8E8E93]">Total raw events</p>
            <p data-testid="fingerspot-total-events" className="num mt-0.5 text-[30px] font-bold leading-none text-[#0071E3]">
              {status?.total_events ?? 0}
            </p>
          </div>
          <div>
            <p className="text-[11.5px] font-semibold uppercase tracking-wide text-[#8E8E93]">Last received</p>
            <p className="mt-0.5 text-[13px] font-medium text-[#1D1D1F]">{fmtTime(status?.last_received_at)}</p>
          </div>
          <button
            data-testid="fingerspot-refresh-btn"
            onClick={load}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#E1E1E6] bg-white/70 py-2 text-[13px] font-medium text-[#1D1D1F] transition-all hover:bg-white active:scale-[0.99]"
          >
            <RefreshCw className="h-4 w-4" /> Refresh now
          </button>
          <p className="text-center text-[11px] text-[#A1A1A6]">Auto-refreshes every 10 seconds</p>
        </div>
      </div>

      <div data-testid="fingerspot-scans" className="liquid-glass-card overflow-hidden">
        <div className="flex items-center gap-2 px-5 pb-3 pt-5">
          <Radio className="h-4 w-4 text-[#0071E3]" />
          <h2 className="text-[15px] font-semibold tracking-tight">Recent Raw Scans</h2>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[#B0B0B8]" />}
        </div>
        {scans.length === 0 ? (
          <p className="py-12 text-center text-[13.5px] text-[#8E8E93]">
            No recent scan events. Tap on the device to test.
          </p>
        ) : (
          <>
          {/* Mobile: stacked cards */}
          <div className="space-y-3 px-4 pb-4 md:hidden">
            {scans.map((s) => (
              <div key={s.id} data-testid="fingerspot-scan-card" className="rounded-2xl border border-black/[0.05] bg-white/70 p-4">
                <div className="flex items-start justify-between gap-2">
                  {s.mapped ? (
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#E9F9EF] text-[#0E8A46]">
                        <UserCheck className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-semibold text-[#1D1D1F]">{s.employee_name}</p>
                        <p className="num text-[11.5px] text-[#8E8E93]">{s.employee_nip || "—"} · PIN {s.employee_pin}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#FFF1E6] text-[#B5590F]">
                        <UserPlus className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold text-[#B5590F]">Unknown Employee</p>
                        <p className="num text-[11.5px] text-[#8E8E93]">PIN {s.employee_pin} · Needs Mapping</p>
                      </div>
                    </div>
                  )}
                  {!s.mapped && (
                    <button
                      data-testid="fingerspot-map-btn-mobile"
                      onClick={() => openMap(s)}
                      className="shrink-0 rounded-full bg-[#0071E3] px-3 py-1.5 text-[12px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.97]"
                    >
                      Map
                    </button>
                  )}
                </div>
                <div className="mt-3 rounded-xl bg-black/[0.03] px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-[#8E8E93]">Scan Time</p>
                  <p className="num mt-0.5 text-[13.5px] font-semibold text-[#1D1D1F]">{s.scan_datetime}</p>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-medium capitalize ${VERIFY_STYLE[s.verification_method] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                    {s.verification_method}
                  </span>
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-medium ${INTERP_STYLE[s.interpretation] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                    {s.interpretation || "—"}
                  </span>
                  <span className="text-[11px] text-[#A1A1A6]">Received {fmtTime(s.received_at)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-y border-black/[0.06] text-[10.5px] uppercase tracking-wider text-[#A1A1A6]">
                  <th className="py-2.5 pl-5 pr-3 font-semibold">Employee</th>
                  <th className="px-3 py-2.5 font-semibold">Scan Time</th>
                  <th className="px-3 py-2.5 font-semibold">Verification</th>
                  <th className="px-3 py-2.5 font-semibold">Interpretation</th>
                  <th className="px-3 py-2.5 font-semibold">Received At</th>
                  <th className="px-3 py-2.5 pr-5 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((s) => (
                  <tr
                    key={s.id}
                    data-testid="fingerspot-scan-row"
                    className="border-b border-black/[0.04] transition-colors last:border-0 hover:bg-white/50"
                  >
                    <td className="py-3 pl-5 pr-3">
                      {s.mapped ? (
                        <div className="flex items-start gap-2.5">
                          <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#E9F9EF] text-[#0E8A46]">
                            <UserCheck className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-medium text-[#1D1D1F]">{s.employee_name}</p>
                            <p className="num text-[11.5px] text-[#8E8E93]">
                              {s.employee_nip || "—"} · PIN {s.employee_pin}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2.5">
                          <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#FFF1E6] text-[#B5590F]">
                            <UserPlus className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-[#B5590F]">Unknown Employee</p>
                            <p className="num text-[11.5px] text-[#8E8E93]">PIN {s.employee_pin} · Needs Mapping</p>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 text-[13px]">{s.scan_datetime}</td>
                    <td className="px-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-medium capitalize ${VERIFY_STYLE[s.verification_method] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                        {s.verification_method}
                      </span>
                    </td>
                    <td className="px-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-medium ${INTERP_STYLE[s.interpretation] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                        {s.interpretation || "—"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 text-[12px] text-[#8E8E93]">{fmtTime(s.received_at)}</td>
                    <td className="whitespace-nowrap px-3 pr-5 text-right">
                      {!s.mapped && (
                        <button
                          data-testid="fingerspot-map-btn"
                          onClick={() => openMap(s)}
                          className="rounded-full bg-[#0071E3] px-3 py-1.5 text-[12px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.97]"
                        >
                          Map Employee
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <Dialog open={!!mapFor} onOpenChange={(v) => { if (!v) setMapFor(null); }}>
        <DialogContent
          data-testid="fingerspot-map-dialog"
          className="gap-0 rounded-[20px] border border-white/50 bg-white/75 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[460px]"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-[18px] font-semibold tracking-tight">Map Employee</DialogTitle>
            <p className="text-[12.5px] text-[#8E8E93]">
              Assign Fingerspot <span className="num font-medium text-[#6E6E73]">PIN {mapFor?.employee_pin}</span> to an employee.
              All past scans with this PIN will automatically show their name.
            </p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="fingerspot-map-select" className="h-11 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]">
                <SelectValue placeholder="Select employee…" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] rounded-xl">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id} disabled={!!e.fingerspot_pin && e.fingerspot_pin !== mapFor?.employee_pin}>
                    {e.nama}
                    {e.emp_code ? ` (${e.emp_code})` : ""}
                    {e.fingerspot_pin ? ` · PIN ${e.fingerspot_pin}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setMapFor(null)}
                className="rounded-full px-4 py-2 text-[13px] font-medium text-[#6E6E73] transition-colors hover:bg-black/[0.04]"
              >
                Cancel
              </button>
              <button
                data-testid="fingerspot-map-confirm"
                onClick={doMap}
                disabled={!pick || saving}
                className="flex items-center gap-1.5 rounded-full bg-[#0071E3] px-4 py-2 text-[13px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} Assign PIN
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, mono, testid }) => (
  <div className="rounded-xl bg-white/60 px-3.5 py-2.5">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8E8E93]">{label}</p>
    <p data-testid={testid} className={`mt-0.5 text-[14px] font-medium text-[#1D1D1F] ${mono ? "font-mono" : ""}`}>{value}</p>
  </div>
);
